import type { ChatOption } from '../types';

/** Shape of a conversation starter chip. */
export interface Starter {
  id: string;
  label: string;
  prompt: string;
}

/**
 * Pool of conversation starters for the RiskSmart Agent. A mix that reflects both
 * of the assistant's jobs: looking across the user's own GRC data, and answering
 * platform-knowledge / risk-management how-to questions (what the old Knowledge
 * chat did, now handled by the one agent). `useChatOptions` shows a random subset
 * (`STARTER_DISPLAY_COUNT`) each time the empty chat appears.
 */
export const AGENT_STARTERS: readonly Starter[] = Object.freeze([
  // Your GRC data
  {
    id: 'agent-top-risks',
    label: 'Top open risks',
    prompt: 'What are my highest-rated open risks right now?',
  },
  {
    id: 'agent-actions-due',
    label: 'Actions due soon',
    prompt: 'What actions are due in the next 30 days?',
  },
  {
    id: 'agent-overdue-controls',
    label: 'Overdue controls',
    prompt: 'Which controls are overdue for testing?',
  },
  {
    id: 'agent-uncontrolled-risks',
    label: 'Risks without controls',
    prompt: 'Which risks have no controls linked to them?',
  },
  // Platform knowledge / how-to
  {
    id: 'agent-inherent-vs-residual',
    label: 'Inherent vs residual risk',
    prompt: 'What is the difference between inherent and residual risk?',
  },
  {
    id: 'agent-using-risksmart',
    label: 'Using RiskSmart',
    prompt: 'How do I set up automated risk monitoring in RiskSmart?',
  },
  {
    id: 'agent-risk-assessment',
    label: 'Risk assessment basics',
    prompt: 'What are the key steps in conducting a risk assessment?',
  },
  {
    id: 'agent-treatment-strategies',
    label: 'Risk treatment options',
    prompt: 'What are the four main risk treatment strategies?',
  },
]);

/** How many starters from the pool to surface at once. */
export const STARTER_DISPLAY_COUNT = 4;

/**
 * Return `count` starters chosen at random from `pool` (Fisher–Yates over a copy,
 * so the pool itself is never mutated). Used to vary the empty-state chips each
 * time the chat opens. Caps at the pool size.
 */
export const pickRandomStarters = (
  pool: readonly Starter[],
  count: number
): Starter[] => {
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }

  return shuffled.slice(0, Math.max(0, Math.min(count, shuffled.length)));
};

export const startersToChatOptions = (
  starters: readonly Starter[],
  sendMessage: (message: string) => Promise<void> | void,
  source: 'dynamic' | 'static'
): ChatOption[] =>
  starters.map((starter) => ({
    id: starter.id,
    label: starter.label,
    action: () => sendMessage(starter.prompt),
    source,
  }));
